import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Badge,
  Card,
  CardBody,
  Heading,
  useToast,
  IconButton,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverCloseButton,
  PopoverBody,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Select,
  Textarea,
  Center,
  Spinner,
  Tooltip,
  Switch,
  Checkbox,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  SimpleGrid,
  Spacer
} from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon, EditIcon, CalendarIcon, InfoIcon } from '@chakra-ui/icons';
import { FiFileText } from 'react-icons/fi';
import { CgNotes } from 'react-icons/cg';
import supabase from '../supabaseClient';
import SessionNotesModal from './SessionNotesModal';
import {
  formatAttendanceStatusLabel,
  getAttendanceStatusColorScheme,
} from '../utils/attendanceDisplay';
import { getLocalDateString, parseLocalDateString } from '../utils/dateUtils';
import {
  canApplyGraceCancellation,
  countGraceCancellationsForYear,
  getGraceCancellationHelperText,
  GRACE_CANCELLATIONS_PER_YEAR,
} from '../utils/graceCancellationUtils';
import {
  isSupposedMakeupSession,
  getSupposedMakeupCancellationAttendance,
} from '../utils/supposedMakeupUtils';

const BulkCancelModal = React.memo(({ isOpen, onClose, onConfirm, selectedDate }) => {
  const [reason, setReason] = useState('');

  // Reset reason when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setReason('');
    }
  }, [isOpen]);

  const handleConfirm = () => {
    onConfirm(reason);
    setReason('');
  };

  // Helper functions for date and time formatting
  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Bulk Cancel Sessions</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            <Text>
              This will cancel all sessions scheduled for {formatDate(selectedDate)}.
            </Text>
            <FormControl isRequired>
              <FormLabel>Reason for Cancellation</FormLabel>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Enter reason for cancellation"
              />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button
            colorScheme="red"
            mr={3}
            onClick={handleConfirm}
            isDisabled={!reason}
          >
            Cancel All Sessions
          </Button>
          <Button variant="ghost" onClick={onClose}>Back</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
});

const CurrentDayDeck = ({ therapistId }) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [temporaryChanges, setTemporaryChanges] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [isDeckConfirmed, setIsDeckConfirmed] = useState(false);
  const [isAttendanceConfirmed, setIsAttendanceConfirmed] = useState(false);
  const [editSession, setEditSession] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [makeupDate, setMakeupDate] = useState(null);
  const [isCancelledByClient, setIsCancelledByClient] = useState(true);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [nextSessionDate, setNextSessionDate] = useState(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isBulkCancelModalOpen, setIsBulkCancelModalOpen] = useState(false);
  const [bulkCancelReason, setBulkCancelReason] = useState('');
  const [noMakeupRequired, setNoMakeupRequired] = useState(false);
  const [graceCancellation, setGraceCancellation] = useState(false);
  const [waivedLateCancellation, setWaivedLateCancellation] = useState(false);
  const [graceCancellationUsedCount, setGraceCancellationUsedCount] = useState(0);
  const [cancelledMakeupByTherapist, setCancelledMakeupByTherapist] = useState(false);
  const [paidNoShowFee, setPaidNoShowFee] = useState(false);
  const toast = useToast();

  const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  const [canBulkAction, setCanBulkAction] = useState(false);

  // Helper function to calculate price with PWD discount and VAT
  const calculatePrice = (baseRate, isPwd, isVatInclusive) => {
    if (!baseRate) return 0;
      
    const VAT_RATE = 0.12;
      
    if (isVatInclusive) {
      const preVatRate = Math.round(baseRate / (1 + VAT_RATE) * 100) / 100;
      if (isPwd) {
        const discountedPreVatRate = Math.round(preVatRate * 0.8 * 100) / 100;
        return Math.round((discountedPreVatRate + (baseRate-preVatRate))* 100) / 100;
      }
      return baseRate;
    }
    return isPwd ? Math.round(baseRate * 0.8 * 100) / 100 : baseRate;
  };

  // Add these state variables to your CurrentDayDeck component
  const [sessionNotes, setSessionNotes] = useState({});
  const [selectedSessionForNotes, setSelectedSessionForNotes] = useState(null);
  const [isSessionNotesModalOpen, setIsSessionNotesModalOpen] = useState(false);
  const [makeupSessions, setMakeupSessions] = useState({});
  
  // New state for pending makeup sessions tab
  const [view, setView] = useState('current'); // 'current' or 'pending-makeup'
  const [pendingMakeupSessions, setPendingMakeupSessions] = useState([]);
  const [pendingMakeupLoading, setPendingMakeupLoading] = useState(false);
  const [makeupDateRange, setMakeupDateRange] = useState(30); // days to look back
  
  // State for makeup optional confirmation modal
  const [isOptionalMakeupModalOpen, setIsOptionalMakeupModalOpen] = useState(false);
  const [selectedOptionalSession, setSelectedOptionalSession] = useState(null);

  // Add this useEffect to check bulk action permissions
  useEffect(() => {
    const checkBulkActionPermissions = async () => {
      try {
        const selectedDateStr = getLocalDateString(selectedDate);
        console.log('Checking bulk action permissions for:', selectedDateStr);
        
        // Check if this is an auto-confirmed date due to leave
        const { data: deckConfirmations, error } = await supabase
          .from('deck_confirmations')
          .select('auto_confirmed')
          .eq('therapist_id', therapistId)
          .eq('date', selectedDateStr);
  
        if (error) {
          console.error('Error checking deck confirmation:', error);
          setCanBulkAction(false);
          return;
        }
  
        // If there's an auto-confirmed record, can't perform bulk actions
        if (deckConfirmations?.length > 0 && deckConfirmations[0].auto_confirmed) {
          console.log('Date is auto-confirmed due to leave');
          setCanBulkAction(false);
          return;
        }
  
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const sessionDate = new Date(selectedDate);
        sessionDate.setHours(0, 0, 0, 0);
  
        // If it's current day, check if any sessions have attendance
        if (sessionDate.getTime() === today.getTime()) {
          const hasNoAttendance = !sessions.some(session => session.attendance);
          console.log('Current day check - Has no attendance:', hasNoAttendance);
          setCanBulkAction(hasNoAttendance);
          return;
        }
  
        // For future dates, always allow
        const isFutureDate = sessionDate > today;
        console.log('Is future date:', isFutureDate);
        setCanBulkAction(isFutureDate);
        
      } catch (error) {
        console.error('Error checking bulk action permissions:', error);
        setCanBulkAction(false);
      }
    };
  
    checkBulkActionPermissions();
  }, [selectedDate, sessions, therapistId]);

  useEffect(() => {
    const initializeDate = async () => {
      try {
        const latestConfirmedDate = await findLatestConfirmedDate();
        if (latestConfirmedDate) {
          // Find the next session date after the latest confirmed date
          const nextDate = await findNextSessionDate(latestConfirmedDate);
          if (nextDate) {
            setSelectedDate(nextDate);
          }
        }
      } catch (error) {
        console.error('Error initializing date:', error);
      }
    };
  
    initializeDate();
  }, [therapistId]); // Only run on mount and therapistId change
  
  // Add this to your useEffect that fetches sessions
  useEffect(() => {
    const resetStatesAndFetch = async () => {
      setIsLoading(true);
      setIsDeckConfirmed(false);
      setIsAttendanceConfirmed(false);
      await fetchSessions();
      await fetchSessionNotes(); // Add this line
      await fetchMakeupSessions(); // Add this line
      await checkDeckConfirmation();
      await checkAttendanceConfirmation();
    };

    resetStatesAndFetch();
  }, [selectedDate, therapistId]);

  // Clear temporary changes when date changes
  useEffect(() => {
    setTemporaryChanges({});
  }, [selectedDate]);

  // Fetch pending makeup sessions when view changes
  useEffect(() => {
    if (view === 'pending-makeup') {
      fetchPendingMakeupSessions();
    }
  }, [view, makeupDateRange, therapistId]);

  useEffect(() => {
    const fetchGraceCancellationCount = async () => {
      if (!isEditModalOpen || !editSession || editSession.temporaryStatus !== 'absent') {
        return;
      }

      const clientId = editSession.client?.id || editSession.client_id;
      if (!clientId) return;

      const selectedDateStr = editSession.attendance_date || getLocalDateString(selectedDate);
      const year = new Date(selectedDateStr).getFullYear();

      try {
        const { data, error } = await supabase
          .from('session_attendance')
          .select('id, grace_cancellation, attendance_date')
          .eq('client_id', clientId)
          .eq('grace_cancellation', true);

        if (error) throw error;

        const currentId = editSession.attendance?.id;
        const filtered = (data || []).filter((row) => row.id !== currentId);
        setGraceCancellationUsedCount(countGraceCancellationsForYear(filtered, year));
      } catch (error) {
        console.error('Error fetching grace cancellation count:', error);
        setGraceCancellationUsedCount(0);
      }
    };

    fetchGraceCancellationCount();
  }, [isEditModalOpen, editSession, selectedDate]);

  // Auto-populate notes for makeup sessions (only for sessions table)
  useEffect(() => {
    // Don't auto-populate notes for makeup in the textarea anymore
    // The makeupNote will be generated separately for the sessions table
    if (editSession?.temporaryStatus !== 'makeup' && editSession?.temporaryStatus !== undefined) {
      // Clear auto-generated makeup notes when switching away from makeup
      if (editSession?.temporaryNotes && editSession.temporaryNotes.includes('makeup session for')) {
        setEditSession(prev => ({
          ...prev,
          temporaryNotes: ''
        }));
      }
    }
  }, [editSession?.temporaryStatus, makeupDate, selectedDate]);

  // Reset toggle state when modal opens or status changes
  useEffect(() => {
    if (editSession?.temporaryStatus === 'cancelled' || editSession?.temporaryStatus === 'absent' || editSession?.temporaryStatus === 'makeup') {
      setIsCancelledByClient(true); // Default to client cancellation/makeup
    }
    
    // Auto-check "No makeup required" for makeup sessions being cancelled
    if (editSession?.temporaryStatus === 'cancelled' && editSession?.isMakeupSession) {
      setNoMakeupRequired(true);
    } else {
      setNoMakeupRequired(false); // Reset checkbox
    }
  }, [editSession?.temporaryStatus, editSession?.isMakeupSession]);

  // Auto-populate notes when scheduling makeup for an already cancelled session
  useEffect(() => {
    const fetchExistingCancellationNotes = async () => {
      if (editSession?.temporaryStatus === 'makeup' && editSession?.id) {
        try {
          const selectedDateStr = getLocalDateString(selectedDate);
          
          // Check for existing cancelled attendance record
          const { data: existingAttendance, error } = await supabase
            .from('session_attendance')
            .select('notes')
            .eq('session_id', editSession.id)
            .eq('attendance_date', selectedDateStr)
            .eq('status', 'cancelled')
            .maybeSingle();

          if (error && error.code !== 'PGNF') {
            console.error('Error fetching existing notes:', error);
            return;
          }

          // If there's an existing cancelled record with notes, auto-populate
          if (existingAttendance?.notes && !editSession.temporaryNotes) {
            setEditSession(prev => ({
              ...prev,
              temporaryNotes: existingAttendance.notes
            }));
          }
        } catch (error) {
          console.error('Error fetching cancellation notes:', error);
        }
      }
    };

    fetchExistingCancellationNotes();
  }, [editSession?.temporaryStatus, editSession?.id, selectedDate]);

  const handleAddSessionNote = async (session, noteDate = selectedDate) => {
    setSelectedSessionForNotes({
      ...session,
      deckDate: getLocalDateString(noteDate),
    });
    setIsSessionNotesModalOpen(true);
  };

  const handleSessionNoteSaved = async () => {
    await fetchSessionNotes(); // Refresh notes indicators
    await fetchSessions(); // Refresh sessions to show any new attendance records
    
    // Also refresh pending makeup sessions if on that tab
    if (view === 'pending-makeup') {
      await fetchPendingMakeupSessions();
    }
    
    toast({
      title: 'Session note saved',
      description: 'The session note has been saved successfully.',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  const fetchSessionNotes = async () => {
    try {
      const selectedDateStr = getLocalDateString(selectedDate);
      
      // Get session notes by original_session_id and date
      const { data: notesData, error: notesError } = await supabase
        .from('session_notes')
        .select('original_session_id, id, session_time')
        .eq('therapist_id', therapistId)
        .eq('session_date', selectedDateStr);
  
      if (notesError) throw notesError;
  
      if (!notesData || notesData.length === 0) {
        setSessionNotes({});
        return;
      }
  
      // Create a map: original_session_id -> note_id
      const notesMap = {};
      notesData.forEach(note => {
        notesMap[note.original_session_id] = note.id;
      });
      
      console.log('Fetched session notes for date:', selectedDateStr);
      console.log('Session notes map:', notesMap);
      
      setSessionNotes(notesMap);
    } catch (error) {
      console.error('Error fetching session notes:', error);
      setSessionNotes({});
    }
  };

  const fetchMakeupSessions = async () => {
    try {
      const selectedDateStr = getLocalDateString(selectedDate);
      
      // Get session attendance records for cancelled sessions on this date to check madeup_for status
      const { data: attendanceData, error: attendanceError } = await supabase
        .from('session_attendance')
        .select('client_id, madeup_for')
        .eq('attendance_date', selectedDateStr)
        .eq('status', 'cancelled');
  
      if (attendanceError) throw attendanceError;
  
      // Get makeup sessions for cancelled sessions on this date
      const { data: makeupData, error: makeupError } = await supabase
        .from('sessions')
        .select('client_id, valid_from, makeup_for, is_makeup')
        .eq('makeup_for', selectedDateStr)
        .eq('is_makeup', true);
  
      if (makeupError) throw makeupError;
  
      // Create a map: client_id -> { makeup_date, madeup_for }
      const makeupMap = {};
      
      // First, add makeup sessions
      if (makeupData && makeupData.length > 0) {
        makeupData.forEach(makeup => {
          makeupMap[makeup.client_id] = {
            makeup_date: makeup.valid_from,
            madeup_for: false // Default to false, will be updated below
          };
        });
      }
      
      // Then, update with madeup_for status from attendance
      if (attendanceData && attendanceData.length > 0) {
        attendanceData.forEach(attendance => {
          if (makeupMap[attendance.client_id]) {
            makeupMap[attendance.client_id].madeup_for = attendance.madeup_for;
          } else {
            // If there's no makeup session but attendance exists, create entry
            if (attendance.madeup_for === false || attendance.madeup_for === null) {
              makeupMap[attendance.client_id] = {
                makeup_date: null,
                madeup_for: attendance.madeup_for
              };
            }
          }
        });
      }
      
      console.log('Fetched makeup sessions for date:', selectedDateStr);
      console.log('Makeup sessions map:', makeupMap);
      
      setMakeupSessions(makeupMap);
    } catch (error) {
      console.error('Error fetching makeup sessions:', error);
      setMakeupSessions({});
    }
  };

  // Replace the fetchPendingMakeupSessions function with this updated version:

  const fetchPendingMakeupSessions = async () => {
    try {
      setPendingMakeupLoading(true);
      
      // Calculate date range for performance optimization
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(endDate.getDate() - makeupDateRange);
      
      const startDateStr = getLocalDateString(startDate);
      const endDateStr = getLocalDateString(endDate);

      const { data, error } = await supabase
        .from('session_attendance')
        .select(`
          *,
          session:sessions!session_id (
            id,
            day,
            time,
            session_type,
            valid_from,
            valid_until,
            client_name
          ),
          client:clients (
            id,
            name,
            nickname,
            age,
            gender,
            diagnosis,
            guardian_name,
            guardian_contact,
            pwd,
            pwd_number,
            notes,
            initial_document,
            given_ca,
            signed_ca,
            signed_cf,
            pwd_card,
            official_receipt,
            or_name,
            client_services (*)
          ),
          therapist:therapists (
            id,
            name,
            specialization
          )
        `)
        .eq('therapist_id', therapistId)
        .in('status', ['cancelled', 'absent'])
        .eq('madeup_for', false)  // Only sessions that require makeup (not null)
        .gte('attendance_date', startDateStr)
        .lte('attendance_date', endDateStr)
        .order('attendance_date', { ascending: false });

      if (error) throw error;

      // Process the data to add calculated fields and ensure proper structure
      const processedSessions = data?.map(record => {
        const matchingService = record.client?.client_services?.find(service => {
          const specializationToService = {
            'Speech Therapy': 'ST',
            'Occupational Therapy': 'OT',
            'Physical Therapy': 'PT',
            'Play Group': 'PG'
          };
          const serviceMatches = service.service_type === specializationToService[record.therapist.specialization];
          const dateMatches = (!service.pricing_valid_from || service.pricing_valid_from <= record.attendance_date) &&
                            (!service.pricing_valid_until || service.pricing_valid_until >= record.attendance_date);
          return serviceMatches && dateMatches;
        });

        return {
          // Session properties
          id: record.session?.id,
          day: record.session?.day,
          time: record.session?.time,
          session_type: record.session?.session_type,
          valid_from: record.session?.valid_from,
          valid_until: record.session?.valid_until,
          
          // Client properties - ensure both client_id and client object are available
          client_id: record.client_id,
          client_name: record.session?.client_name || record.client?.name,
          client: record.client,
          
          // Therapist properties
          therapist: record.therapist,
          
          // Attendance properties
          attendance: {
            id: record.id,
            session_id: record.session_id,
            client_id: record.client_id,
            therapist_id: record.therapist_id,
            attendance_date: record.attendance_date,
            status: record.status,
            notes: record.notes,
            base_rate: record.base_rate,
            pwd_discount: record.pwd_discount,
            final_amount: record.final_amount,
            session_type: record.session_type,
            promo_session: record.promo_session,
            amount_paid: record.amount_paid,
            or_number: record.or_number,
            payment_date: record.payment_date,
            is_paid: record.is_paid,
            madeup_for: record.madeup_for
          },
          
          // Calculated properties
          base_rate: matchingService?.base_rate || 0,
          discounted_rate: calculatePrice(
            matchingService?.base_rate || 0,
            record.client?.pwd || false,
            matchingService?.vat_inclusive || false
          ),
          service_type: matchingService?.service_type || 'N/A',
          
          // Add attendance_date for grouping
          attendance_date: record.attendance_date
        };
      }) || [];

      console.log('Processed pending makeup sessions:', processedSessions);
      setPendingMakeupSessions(processedSessions);
    } catch (error) {
      toast({
        title: 'Error fetching pending makeup sessions',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setPendingMakeupLoading(false);
    }
  };

  const handleMarkMakeupOptional = async () => {
    try {
      if (!selectedOptionalSession) return;

      const { error } = await supabase
        .from('session_attendance')
        .update({ madeup_for: null })
        .eq('id', selectedOptionalSession.attendance.id);

      if (error) throw error;

      toast({
        title: 'Makeup marked as optional',
        description: 'This session has been removed from the required makeup list.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Refresh the pending makeup sessions list
      await fetchPendingMakeupSessions();
      
      // Close modal and reset state
      setIsOptionalMakeupModalOpen(false);
      setSelectedOptionalSession(null);
    } catch (error) {
      toast({
        title: 'Error updating makeup status',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const findLatestConfirmedDate = async () => {
    try {
      const { data, error } = await supabase
        .from('attendance_confirmations')
        .select('date')
        .eq('therapist_id', therapistId)
        .order('date', { ascending: false })
        .limit(1);

      if (error) throw error;
      return data?.[0]?.date ? parseLocalDateString(data[0].date) : null;
    } catch (error) {
      console.error('Error finding latest confirmed date:', error);
      return null;
    }
  };

  const canConfirmAttendance = () => {
    // If no sessions, don't allow confirmation
    if (sessions.length === 0) return false;
  
    // Can't confirm future dates
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const sessionDate = new Date(selectedDate);
    sessionDate.setHours(0, 0, 0, 0);
    if (sessionDate > today) return false;
  
    // All sessions must have attendance marked
    return sessions.every(session => session.attendance !== undefined);
  };

  const canPerformBulkActions = async () => {
    try {
      const selectedDateStr = getLocalDateString(selectedDate);
      
      // Check if this is an auto-confirmed date due to leave
      const { data: deckConfirmation } = await supabase
        .from('deck_confirmations')
        .select('auto_confirmed')
        .eq('therapist_id', therapistId)
        .eq('date', selectedDateStr)
        .single();
  
      // If auto-confirmed due to leave, can't perform bulk actions
      if (deckConfirmation?.auto_confirmed) return false;
  
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const sessionDate = new Date(selectedDate);
      sessionDate.setHours(0, 0, 0, 0);
  
      // If it's current day, check if any sessions have attendance
      if (sessionDate.getTime() === today.getTime()) {
        return !sessions.some(session => session.attendance);
      }
  
      // For future dates, always allow
      return sessionDate >= today;
    } catch (error) {
      console.error('Error checking bulk action permissions:', error);
      return false;
    }
  };
  
  // Add function to handle bulk cancellation
  const handleBulkCancel = async (reason) => {
    try {
      const selectedDateStr = getLocalDateString(selectedDate);
  
      // Create attendance records for all sessions
      const attendanceRecords = sessions.map(session => ({
        session_id: session.id,
        client_id: session.client_id,
        therapist_id: therapistId,
        attendance_date: selectedDateStr,
        status: 'cancelled',
        notes: `Therapist cancelled: ${reason}`,
        base_rate: 0,
        final_amount: 0,
        madeup_for: null // Therapist cancellation = optional makeup
      }));
  
      const { error } = await supabase
        .from('session_attendance')
        .insert(attendanceRecords);
  
      if (error) throw error;
  
      // Close modal and refresh
      setIsBulkCancelModalOpen(false);
      await fetchSessions();
      await fetchMakeupSessions(); // Refresh makeup badges
  
      toast({
        title: 'Sessions Cancelled',
        description: `Successfully cancelled ${sessions.length} session(s)`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error bulk cancelling sessions:', error);
      toast({
        title: 'Error',
        description: 'Failed to cancel sessions',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };
  
  // Add function to handle bulk uncancellation
  const handleBulkUncancel = async () => {
    try {
      const selectedDateStr = getLocalDateString(selectedDate);
  
      // Delete all cancellation records for this date
      const { error } = await supabase
        .from('session_attendance')
        .delete()
        .eq('therapist_id', therapistId)
        .eq('attendance_date', selectedDateStr)
        .eq('status', 'cancelled')
        .like('notes', 'Bulk cancelled:%');
  
      if (error) throw error;
  
      await fetchSessions();
      await fetchMakeupSessions(); // Refresh makeup badges
  
      toast({
        title: 'Sessions Uncancelled',
        description: 'Successfully uncancelled all sessions',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error uncancelling sessions:', error);
      toast({
        title: 'Error',
        description: 'Failed to uncancel sessions',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const fetchSessions = async () => {
    console.log('Therapist ID: ', therapistId);
    try {
      setIsLoading(true);
      const dayOfWeek = DAYS[selectedDate.getDay()];
      const selectedDateStr = getLocalDateString(selectedDate);
  
      // Fetch sessions first - explicit columns only
      const { data: sessionsData, error: sessionsError } = await supabase
        .from('sessions')
        .select('id, therapist_id, client_id, client_name, day, time, session_type, notes, valid_from, valid_until, is_makeup, makeup_for, session_attendance_id')
        .eq('therapist_id', therapistId)
        .eq('day', dayOfWeek)
        .lte('valid_from', selectedDateStr)
        .or(`valid_until.is.null,valid_until.gte.${selectedDateStr}`);
  
      if (sessionsError) throw sessionsError;
  
      // Fetch clients separately
      const clientIds = [...new Set(sessionsData?.map(s => s.client_id) || [])];
      let clientsData = [];
      
      if (clientIds.length > 0) {
        const { data: clients, error: clientsError } = await supabase
          .from('clients')
          .select('id, name, nickname, age, gender, diagnosis, guardian_name, guardian_contact, pwd, pwd_number, notes, initial_document, given_ca, signed_ca, signed_cf, pwd_card, official_receipt, or_name')
          .in('id', clientIds);
        
        if (clientsError) throw clientsError;
        clientsData = clients || [];
      }
  
      // Fetch attendance separately - explicit columns only
      const { data: attendanceData, error: attendanceError } = await supabase
        .from('session_attendance')
        .select('id, session_id, client_id, therapist_id, attendance_date, status, notes, base_rate, pwd_discount, final_amount, session_type, promo_session, amount_paid, or_number, payment_date, is_paid, madeup_for, paid_noshow, makeup_scheduled, makeup_fee_voided_by_therapist, voided_linked_makeup_on, grace_cancellation, waived_late_cancellation')
        .eq('attendance_date', selectedDateStr)
        .in('session_id', sessionsData.map(s => s.id));
  
      if (attendanceError) throw attendanceError;
  
      // Process and sort sessions with manual joins
      const processedSessions = sessionsData
        .map(session => {
          // Find the client for this session
          const client = clientsData.find(c => c.id === session.client_id);
          
          // Find attendance for this session
          const attendance = attendanceData?.find(
            a => a.session_id === session.id
          );
          
          const isMakeupSession = isSupposedMakeupSession(session);
          
          let makeupNote = null;
          if (isMakeupSession && session.notes) {
            const noteMatch = session.notes.match(/makeup session for (.+)/i);
            if (noteMatch) {
              makeupNote = `Makeup for cancelled session (${noteMatch[1]})`;
            } else {
              makeupNote = session.notes;
            }
          }
          
          return {
            ...session,
            client: client || { name: 'Unknown Client', nickname: null }, // Fallback for missing client
            attendance,
            isMakeupSession,
            makeupNote
          };
        })
        .sort((a, b) => {
          const timeA = parseInt(a.time.split(':')[0]);
          const timeB = parseInt(b.time.split(':')[0]);
          return timeA - timeB;
        });
  
      setSessions(processedSessions);
    } catch (error) {
      console.error('Error fetching sessions:', error);
      toast({
        title: 'Error fetching sessions',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const isWithinOneDayOfSession = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const sessionDate = new Date(date);
    sessionDate.setHours(0, 0, 0, 0);
  
    const diffTime = sessionDate.getTime() - today.getTime();
    const diffDays = diffTime / (1000 * 60 * 60 * 24);
  
    return diffDays <= 1;
  };
  
  const checkAllSessionsMarked = (sessions) => {
    // Return true only if all sessions have attendance records
    return sessions.every(session => session.attendance !== undefined);
  };

  const sortTimes = (times) => {
    return times.sort((a, b) => {
      const hourA = parseInt(a.split(':')[0]);
      const hourB = parseInt(b.split(':')[0]);
      return hourA - hourB;
    });
  };

  const getSessionSummary = () => {
    const summary = {
      IE: 0,
      Endorsement: 0,
      Regular: 0
    };
  
    sessions.forEach(session => {
      if (session.attendance?.status === 'present') {
        summary[session.session_type] = (summary[session.session_type] || 0) + 1;
      }
    });
  
    return summary;
  };

  const SessionSummaryModal = ({ isOpen, onClose, onConfirm }) => {
    const summary = getSessionSummary();
  
    return (
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm Daily Sessions</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="start" spacing={4}>
              <Text fontWeight="bold">Today's Session Summary:</Text>
              <VStack align="start" pl={4} spacing={2}>
                {summary.IE > 0 && (
                  <Text>{summary.IE} Initial Evaluation{summary.IE > 1 ? 's' : ''} (IE)</Text>
                )}
                {summary.Endorsement > 0 && (
                  <Text>{summary.Endorsement} Endorsement{summary.Endorsement > 1 ? 's' : ''}</Text>
                )}
                {summary.Regular > 0 && (
                  <Text>{summary.Regular} Regular Session{summary.Regular > 1 ? 's' : ''}</Text>
                )}
              </VStack>
              {Object.values(summary).every(val => val === 0) && (
                <Text color="orange.500">No sessions marked as present today.</Text>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={onConfirm}>
              Confirm Attendance
            </Button>
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  };

  const checkDeckConfirmation = async () => {
    try {
      const selectedDateStr = getLocalDateString(selectedDate);
      const { data, error } = await supabase
        .from('deck_confirmations')
        .select('*')
        .eq('therapist_id', therapistId)
        .eq('date', selectedDateStr)
        .single();

      if (error && error.code !== 'PGNF') throw error;
      setIsDeckConfirmed(!!data);
    } catch (error) {
      console.error('Error checking deck confirmation:', error);
    }
  };

  const checkAttendanceConfirmation = async () => {
    try {
      const selectedDateStr = getLocalDateString(selectedDate);
      const { data, error } = await supabase
        .from('attendance_confirmations')
        .select('*')
        .eq('therapist_id', therapistId)
        .eq('date', selectedDateStr)
        .single();
  
      if (error && error.code !== 'PGNF') throw error;
  
      // Simply set the confirmation status without navigating
      setIsAttendanceConfirmed(!!data);
    } catch (error) {
      console.error('Error checking attendance confirmation:', error);
    }
  };

  const handleEditSession = (session) => {
    setEditSession(session);
    setGraceCancellation(Boolean(session.attendance?.grace_cancellation));
    setWaivedLateCancellation(Boolean(session.attendance?.waived_late_cancellation));
    setCancelledMakeupByTherapist(Boolean(session.attendance?.makeup_fee_voided_by_therapist));
    setPaidNoShowFee(session.attendance?.paid_noshow === true);
    setIsEditModalOpen(true);
  };

  // Replace the handleSaveEdit function with this updated version that handles both session structures:

  const handleSaveEdit = async () => {
    try {
      // Determine which date to use based on session structure
      const selectedDateStr = editSession.attendance_date || getLocalDateString(selectedDate);

      console.log('Date: ', selectedDateStr);
      console.log('Edit Session ID: ', editSession.id);
      
      // For pending makeup sessions, we already have the attendance record
      // For current day sessions, we need to check for existing attendance
      let existingAttendance = null;
      
      if (editSession.attendance_date) {
        // This is from pending makeup tab - attendance already exists
        existingAttendance = editSession.attendance;
        console.log('Using existing attendance from pending makeup:', existingAttendance);
      } else {
        // This is from current day tab - check for existing attendance
        const { data, error: fetchError } = await supabase
          .from('session_attendance')
          .select('*')
          .eq('session_id', editSession.id)
          .eq('attendance_date', selectedDateStr)
          .maybeSingle();

        if (fetchError && fetchError.code !== 'PGNF') throw fetchError;
        existingAttendance = data;
        console.log('Fetched existing attendance for current day:', existingAttendance);
      }

      // Handle Re-Opening session (removing attendance)
      if (!editSession.temporaryStatus) {
        console.log('Re-Opening Session');
        if (existingAttendance) {
          // Delete existing attendance record
          const { error: deleteError } = await supabase
            .from('session_attendance')
            .delete()
            .eq('id', existingAttendance.id);

          if (deleteError) throw deleteError;

          console.log('DELETED');
        }
        // Reset states and refresh
        setTemporaryChanges(prev => {
          const newChanges = { ...prev };
          delete newChanges[`${editSession.id}-${selectedDateStr}`];
          return newChanges;
        });
      } 
      // Handle makeup session creation/update
      else if (editSession.temporaryStatus === 'makeup' && makeupDate) {
        console.log('Creating/Updating Makeup Session');
        
        // Determine the correct client_id based on session structure
        const clientId = editSession.client_id || editSession.client?.id;
        
        if (!clientId) {
          throw new Error('Unable to determine client ID for this session');
        }
        
        // First, update or insert attendance record (use form notes) and get its ID
        const attendanceData = {
          session_id: editSession.id,
          client_id: clientId,
          therapist_id: therapistId,
          attendance_date: selectedDateStr,
          status: 'cancelled',
          notes: editSession.temporaryNotes,
          base_rate: 0,
          final_amount: 0,
          madeup_for: isCancelledByClient ? (noMakeupRequired ? null : false) : null,
          makeup_scheduled: isCancelledByClient ? true : null // Set to true when makeup is scheduled
        };

        let attendanceRecordId;
        
        if (existingAttendance) {
          const { error: updateError } = await supabase
            .from('session_attendance')
            .update(attendanceData)
            .eq('id', existingAttendance.id);
          if (updateError) throw updateError;
          attendanceRecordId = existingAttendance.id;
        } else {
          const { data: newAttendance, error: insertError } = await supabase
            .from('session_attendance')
            .insert([attendanceData])
            .select('id')
            .single();
          if (insertError) throw insertError;
          attendanceRecordId = newAttendance.id;
        }

        // Generate note for sessions table (different format)
        const originalDate = editSession.attendance_date ? new Date(editSession.attendance_date) : selectedDate;
        const sessionsTableNote = `Makeup for cancelled session (${formatDate(originalDate)})`;

        // Get client name - handle both session structures
        const clientName = editSession.client_name || editSession.client?.name;

        // Format time without leading zero (e.g., "9:00" not "09:00")
        const rawTime = editSession.temporaryTime;
        const hour = parseInt(rawTime.split(':')[0]);
        const makeupTime = `${hour}:00`;

        // Create new makeup session with reference to the attendance record
        const { error: sessionError } = await supabase
          .from('sessions')
          .insert([{
            therapist_id: therapistId,
            client_id: clientId,
            client_name: clientName,
            day: DAYS[new Date(makeupDate).getDay()],
            time: makeupTime,
            session_type: editSession.session_type,
            notes: sessionsTableNote,
            valid_from: makeupDate,
            valid_until: makeupDate,
            is_makeup: isCancelledByClient ? true : null,
            makeup_for: isCancelledByClient ? selectedDateStr : null,
            session_attendance_id: isCancelledByClient ? attendanceRecordId : null
          }]);

        if (sessionError) throw sessionError;

        // Mark the slot as assigned (is_open = null) in makeup_session_availability
        const { error: slotUpdateError } = await supabase
          .from('makeup_session_availability')
          .update({ 
            is_open: null,
            updated_at: new Date().toISOString()
          })
          .eq('therapist_id', therapistId)
          .eq('date', makeupDate)
          .eq('time_slot', makeupTime);

        if (slotUpdateError) {
          console.error('Error updating slot availability:', slotUpdateError);
        }

        setTemporaryChanges(prev => ({
          ...prev,
          [`${editSession.id}-${selectedDateStr}`]: {
            new_status: 'cancelled',
            notes: editSession.temporaryNotes,
            makeup_date: makeupDate,
            makeup_time: editSession.temporaryTime
          }
        }));
      } 
      // Handle other status changes (absent, cancelled without makeup)
      else {
        console.log('Updating Session Status');
        
        // Determine the correct client_id based on session structure
        const clientId = editSession.client_id || editSession.client?.id;
        
        if (!clientId) {
          throw new Error('Unable to determine client ID for this session');
        }
        
        const isMakeupSession = isSupposedMakeupSession(editSession);
        const waiveMakeupByTherapist =
          isMakeupSession &&
          (editSession.temporaryStatus === 'cancelled' ||
            editSession.temporaryStatus === 'absent') &&
          cancelledMakeupByTherapist;

        let madeupForValue = null;
        let makeupScheduledValue = null;
        let finalAmount = 0;
        let isPaidValue = false;
        let amountPaidValue = null;
        let paidNoshowValue = null;
        let makeupFeeVoidedByTherapist = null;

        if (isMakeupSession &&
            (editSession.temporaryStatus === 'cancelled' ||
              editSession.temporaryStatus === 'absent')) {
          if (editSession.temporaryStatus === 'absent' && graceCancellation) {
            const { allowed } = canApplyGraceCancellation(graceCancellationUsedCount, true);
            if (!allowed) {
              toast({
                title: 'Grace cancellation limit reached',
                description: `This client has already used all ${GRACE_CANCELLATIONS_PER_YEAR} grace cancellations this year.`,
                status: 'error',
                duration: 5000,
              });
              return;
            }
          }

          const makeupFields = getSupposedMakeupCancellationAttendance({
            status: editSession.temporaryStatus,
            cancelledByTherapist: cancelledMakeupByTherapist,
            paidNoShowFee,
          });

          madeupForValue = makeupFields.madeupForValue;
          makeupScheduledValue = makeupFields.makeupScheduledValue;
          paidNoshowValue = makeupFields.paidNoshow;
          makeupFeeVoidedByTherapist = makeupFields.makeupFeeVoidedByTherapist;

          if (editSession.temporaryStatus === 'absent') {
            if (graceCancellation || waivedLateCancellation) {
              finalAmount = 0;
              amountPaidValue = null;
              isPaidValue = false;
            } else {
              finalAmount = makeupFields.finalAmount;
              isPaidValue = makeupFields.isPaidValue;
              amountPaidValue = makeupFields.amountPaidValue;
            }
          } else {
            finalAmount = makeupFields.finalAmount;
            isPaidValue = makeupFields.isPaidValue;
            amountPaidValue = makeupFields.amountPaidValue;
          }
        } else if (editSession.temporaryStatus === 'cancelled' || editSession.temporaryStatus === 'absent') {
          if (editSession.temporaryStatus === 'absent') {
            if (graceCancellation) {
              const { allowed } = canApplyGraceCancellation(graceCancellationUsedCount, true);
              if (!allowed) {
                toast({
                  title: 'Grace cancellation limit reached',
                  description: `This client has already used all ${GRACE_CANCELLATIONS_PER_YEAR} grace cancellations this year.`,
                  status: 'error',
                  duration: 5000,
                });
                return;
              }
            }
            madeupForValue = false;
            makeupScheduledValue = false;
            finalAmount = graceCancellation || waivedLateCancellation ? 0 : 950;
          } else if (isCancelledByClient && !noMakeupRequired) {
            madeupForValue = false;
            makeupScheduledValue = false;
          }
        }

        const attendanceData = {
          session_id: editSession.id,
          client_id: clientId,
          therapist_id: therapistId,
          attendance_date: selectedDateStr,
          status: editSession.temporaryStatus,
          notes: editSession.temporaryNotes,
          base_rate: 0,
          final_amount: finalAmount,
          madeup_for: madeupForValue,
          makeup_scheduled: makeupScheduledValue,
          grace_cancellation:
            editSession.temporaryStatus === 'absent' ? Boolean(graceCancellation) : false,
          waived_late_cancellation:
            editSession.temporaryStatus === 'absent' ? Boolean(waivedLateCancellation) : false,
          paid_noshow: paidNoshowValue,
          makeup_fee_voided_by_therapist: makeupFeeVoidedByTherapist,
          is_paid: isPaidValue,
          amount_paid: amountPaidValue,
          payment_date: paidNoShowFee ? new Date().toISOString() : null,
        };

        console.log('Attendance Data: ', attendanceData);

        if (existingAttendance) {
          const { error: updateError } = await supabase
            .from('session_attendance')
            .update(attendanceData)
            .eq('id', existingAttendance.id);
          if (updateError) throw updateError;
        } else {
          const { error: insertError } = await supabase
            .from('session_attendance')
            .insert([attendanceData]);
          if (insertError) throw insertError;
        }

        if (isMakeupSession &&
            (editSession.temporaryStatus === 'cancelled' ||
              editSession.temporaryStatus === 'absent') &&
            editSession.session_attendance_id) {
          if (waiveMakeupByTherapist) {
            const { error: voidOriginalErr } = await supabase
              .from('session_attendance')
              .update({
                madeup_for: null,
                makeup_scheduled: null,
                voided_linked_makeup_on: selectedDateStr,
              })
              .eq('id', editSession.session_attendance_id);

            if (voidOriginalErr) {
              console.error('Error voiding makeup link on original session:', voidOriginalErr);
            }

            const { error: deleteMakeupSlotErr } = await supabase
              .from('sessions')
              .delete()
              .eq('session_attendance_id', editSession.session_attendance_id)
              .eq('is_makeup', true)
              .eq('valid_from', selectedDateStr);

            if (deleteMakeupSlotErr) {
              console.error('Error removing supposed-makeup calendar row:', deleteMakeupSlotErr);
            }
          } else if (paidNoShowFee) {
            const { error: originalUpdateError } = await supabase
              .from('session_attendance')
              .update({
                madeup_for: null,
                makeup_scheduled: null,
                voided_linked_makeup_on: null,
              })
              .eq('id', editSession.session_attendance_id);

            if (originalUpdateError) {
              console.error('Error updating original session after no-show fee:', originalUpdateError);
            }
          }
        }

        setTemporaryChanges(prev => ({
          ...prev,
          [`${editSession.id}-${selectedDateStr}`]: {
            new_status: editSession.temporaryStatus,
            notes: editSession.temporaryNotes
          }
        }));
      }

      // Clear modal state
      setIsEditModalOpen(false);
      setMakeupDate(null);
      setEditSession(null);
      setIsCancelledByClient(true);
      setNoMakeupRequired(false);
      setGraceCancellation(false);
      setGraceCancellationUsedCount(0);
      setCancelledMakeupByTherapist(false);
      setPaidNoShowFee(false);
      
      // Fetch updated sessions to refresh the view
      await fetchSessions();
      await fetchMakeupSessions(); // Refresh makeup sessions data
      
      // Also refresh pending makeup sessions if on that tab
      if (view === 'pending-makeup') {
        await fetchPendingMakeupSessions();
      }

      toast({
        title: 'Changes saved',
        description: editSession.temporaryStatus === 'makeup' ? 
          'Makeup session scheduled and original session marked as cancelled' : 
          editSession.temporaryStatus ? 
            'Session status updated' : 
            'Session re-opened',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error saving changes:', error);
      toast({
        title: 'Error saving changes',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleConfirmDeck = async () => {
    try {
      const selectedDateStr = getLocalDateString(selectedDate);
      
      const { error } = await supabase
        .from('deck_confirmations')
        .insert({
          therapist_id: therapistId,
          date: selectedDateStr,
          confirmed_at: new Date().toISOString()
        });

      if (error) throw error;

      setIsDeckConfirmed(true);
      toast({
        title: 'Deck confirmed',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error confirming deck:', error);
      toast({
        title: 'Error confirming deck',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Add this function to get the next session date
  const findNextSessionDate = async (startDate) => {
    try {
      const startDateStr = getLocalDateString(startDate);
      
      // Fetch all valid sessions
      const { data: sessionsData, error } = await supabase
        .from('sessions')
        .select('*')
        .eq('therapist_id', therapistId)
        .order('valid_from', { ascending: true });
  
      if (error) throw error;
      if (!sessionsData?.length) return null;
  
      // Start checking from the day after current date
      let checkDate = new Date(startDate);
      checkDate.setDate(checkDate.getDate() + 1);
  
      while (checkDate.getTime() < startDate.getTime() + (365 * 24 * 60 * 60 * 1000)) { // Look up to 1 year ahead
        const checkDateStr = getLocalDateString(checkDate);
        const dayOfWeek = DAYS[checkDate.getDay()];
  
        // Check if there's a valid session on this day
        const validSession = sessionsData.find(session => {
          const isValidDay = session.day === dayOfWeek;
          const isAfterValidFrom = new Date(session.valid_from) <= checkDate;
          const isBeforeValidUntil = !session.valid_until || new Date(session.valid_until) >= checkDate;
          
          return isValidDay && isAfterValidFrom && isBeforeValidUntil;
        });
  
        if (validSession) {
          console.log('Found next session date:', checkDate);
          return checkDate;
        }
  
        checkDate.setDate(checkDate.getDate() + 1);
      }
  
      return null;
    } catch (error) {
      console.error('Error finding next session date:', error);
      return null;
    }
  };
  
  // Add a success modal component
  const SuccessModal = ({ isOpen, onClose, currentDate, nextDate }) => (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalBody p={6}>
          <VStack spacing={4}>
            <Box textAlign="center">
              <Text color="green.500" fontWeight="bold" mb={2}>
                ✓ Attendance Confirmed
              </Text>
              <Text>
                Attendance confirmed for {formatDate(currentDate)}
              </Text>
              {nextDate ? (
                <Text mt={4}>
                  Navigating to next session date:
                  <Text fontWeight="bold" mt={1}>
                    {formatDate(nextDate)}
                  </Text>
                </Text>
              ) : (
                <Text mt={4} color="orange.500">
                  No future sessions found
                </Text>
              )}
            </Box>
            <Spinner color="blue.500" />
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
  
  // Update the handleConfirmAttendance function
  const handleConfirmAttendance = async () => {
    try {
      const selectedDateStr = getLocalDateString(selectedDate);
      
      const { error } = await supabase
        .from('attendance_confirmations')
        .insert({
          therapist_id: therapistId,
          date: selectedDateStr,
          confirmed_at: new Date().toISOString()
        });
  
      if (error) throw error;
  
      setIsAttendanceConfirmed(true);
      setIsConfirmModalOpen(false);
      
      // Find next session date
      const nextDate = await findNextSessionDate(selectedDate);
      setNextSessionDate(nextDate);
      
      // Show success modal
      setIsSuccessModalOpen(true);
  
      // After modal display, update states and navigate
      setTimeout(() => {
        setIsSuccessModalOpen(false);
        if (nextDate) {
          setIsDeckConfirmed(false);
          setIsAttendanceConfirmed(false);
          setSelectedDate(nextDate);
        }
      }, 2000);
  
    } catch (error) {
      console.error('Error confirming attendance:', error);
      toast({
        title: 'Error confirming attendance',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Rest of the component implementation...
  // Include date navigation, session cards rendering, etc.

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  
  const formatTime = (time) => {
    const hour = parseInt(time.split(':')[0]);
    const h = hour % 12 || 12;
    const ampm = hour < 12 ? 'AM' : 'PM';
    return `${h}:00 ${ampm}`;
  };

  // Updated SessionCard component
  const SessionCard = ({ session, temporaryChanges, selectedDate, onEdit, onAddNote, isDeckConfirmed, hasNote, makeupSessions, isPendingMakeup, onMarkOptional }) => {
    const selectedDateStr = getLocalDateString(selectedDate);
    const hasTemporaryChange = temporaryChanges[`${session.id}-${selectedDateStr}`];
    const status = session.attendance?.status || 'pending';
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const sessionDate = new Date(selectedDate);
    sessionDate.setHours(0, 0, 0, 0);
    const isPastOrToday = sessionDate <= today;

    return (
        <Card 
          size="sm" 
          variant="outline"
          bg={status === 'present' ? 'green.50' : status === 'pending' ? 'white' : 'red.50'}
          borderColor={hasTemporaryChange ? 'yellow.400' : 'gray.200'}
          width="100%"
        >
          <CardBody py={2}>
            <VStack align="stretch" spacing={2}>
              {/* Client name and badges */}
              <VStack align="start" spacing={1}>
                <HStack justify="space-between" w="100%">
                  <Text 
                    fontWeight="bold" 
                    fontSize={{ base: "sm", md: "md" }}
                    noOfLines={1}
                    flex={1}
                  >
                    {session.client.name}
                  </Text>
                  
                  {/* Session Note Indicator */}
                  {hasNote && isPastOrToday && (
                    <Tooltip label="Session note completed">
                      <Badge colorScheme="purple" variant="solid" fontSize="xs">
                        <HStack spacing={1}>
                          <FiFileText size="10px" />
                          <Text>Note</Text>
                        </HStack>
                      </Badge>
                    </Tooltip>
                  )}
                </HStack>
                
                {session.client.nickname && (
                  <Text 
                    fontSize="sm" 
                    color="gray.500"
                    mt={0}
                  >
                    "{session.client.nickname}"
                  </Text>
                )}
                
                {/* Badges in vertical stack on mobile */}
                <HStack 
                  spacing={2} 
                  flexWrap="wrap"
                  mt={1}
                >
                  <Badge 
                    colorScheme={
                      session.attendance?.session_type === 'Regular' || 
                      (!session.attendance && session.session_type === 'Regular')
                        ? 'blue' 
                        : 'green'
                    }
                  >
                    {session.attendance?.session_type || session.session_type}
                  </Badge>
                  
                  {session.isMakeupSession && (
                    <Badge colorScheme="purple">
                      {(session.attendance?.status === 'cancelled' || hasTemporaryChange?.new_status === 'cancelled')
                        ? `Supposed Makeup Session for ${session.makeupNote ? session.makeupNote.match(/\(([^)]+)\)/)?.[1] || 'Unknown Date' : 'Unknown Date'}`
                        : 'Makeup Session'
                      }
                    </Badge>
                  )}
                  
                  {hasTemporaryChange && (
                    <Badge colorScheme="yellow">Modified</Badge>
                  )}
                  
                  {(session.attendance || hasTemporaryChange) && (
                    <Badge
                      colorScheme={getAttendanceStatusColorScheme(
                        session.attendance?.status ?? hasTemporaryChange.new_status
                      )}
                    >
                      {formatAttendanceStatusLabel(
                        session.attendance?.status ?? hasTemporaryChange.new_status
                      )}
                    </Badge>
                  )}
                  
                  {/* Makeup Status Badge for Cancelled Sessions */}
                  {(session.attendance?.status === 'cancelled' ||
                    session.attendance?.status === 'absent' ||
                    hasTemporaryChange?.new_status === 'cancelled' ||
                    hasTemporaryChange?.new_status === 'absent') &&
                    !session.isMakeupSession && (
                    <Badge 
                      colorScheme={
                        makeupSessions[session.client_id] && makeupSessions[session.client_id].makeup_date
                          ? (makeupSessions[session.client_id].madeup_for ? 'green' : 'blue')
                          : (makeupSessions[session.client_id] && makeupSessions[session.client_id].madeup_for === null
                              ? 'gray' 
                              : 'orange')
                      }
                      variant="solid"
                    >
                      {makeupSessions[session.client_id] 
                        ? (makeupSessions[session.client_id].makeup_date 
                            ? (makeupSessions[session.client_id].madeup_for 
                                ? `Successfully Madeup on ${new Date(makeupSessions[session.client_id].makeup_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` 
                                : `Pending Makeup on ${new Date(makeupSessions[session.client_id].makeup_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`)
                            : (makeupSessions[session.client_id].madeup_for === null 
                                ? 'Makeup Pending (Optional)' 
                                : 'Makeup Pending'))
                        : 'Makeup Pending'}
                    </Badge>
                  )}
                  
                  {/* Pending No-Show Fee Badge for Cancelled Makeup Sessions */}
                  {(session.attendance?.status === 'cancelled' || session.attendance?.status === 'absent' || 
                    hasTemporaryChange?.new_status === 'cancelled' || hasTemporaryChange?.new_status === 'absent') && 
                    session.isMakeupSession && (
                    <Badge colorScheme="red" variant="solid">
                      Charged No-Show Fee
                    </Badge>
                  )}
                </HStack>
              </VStack>

              {/* Action Buttons */}
              <HStack spacing={2} flexWrap="wrap">
                {/* Modify Session Button - HIDDEN: Therapists can no longer cancel/schedule makeups directly */}
                {false && (!isDeckConfirmed || (session.attendance && 
                  (session.attendance.status === 'cancelled' || 
                  session.attendance.status === 'absent'))) && (
                  <Button
                    size="sm"
                    leftIcon={<EditIcon />}
                    onClick={() => onEdit(session)}
                    colorScheme="blue"
                    variant="outline"
                    flex={{ base: "1", md: "0" }}
                    minW="200px"
                  >
                    Modify Session
                  </Button>
                )}

                {/* Conditional button based on context */}
                {isPendingMakeup ? (
                  <Button
                    size="sm"
                    leftIcon={<InfoIcon />}
                    onClick={() => onMarkOptional(session)}
                    colorScheme="orange"
                    variant="outline"
                    flex={{ base: "1", md: "0" }}
                    minW="200px"
                  >
                    Mark Makeup as Optional
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    leftIcon={<FiFileText />}
                    onClick={() => onAddNote(session, selectedDate)}
                    colorScheme={hasNote ? "purple" : "green"}
                    variant={hasNote ? "solid" : "outline"}
                    flex={{ base: "1", md: "0" }}
                    minW="200px"
                  >
                    {hasNote ? "View Session Notes" : "Add Session Notes"}
                  </Button>
                )}
              </HStack>

              {/* Notes section */}
              {(hasTemporaryChange?.notes || session.attendance?.notes || session.makeupNote) && (
                <Text fontSize="sm" color="gray.600">
                  {hasTemporaryChange?.notes || session.attendance?.notes || session.makeupNote}
                </Text>
              )}
            </VStack>
          </CardBody>
        </Card>
    );
  };
  
  // Update the renderTimeSlots function to pass the new props
  const renderTimeSlots = () => {
    // Get unique times from sessions and sort them
    const times = sortTimes([...new Set(sessions.map(s => s.time))]);
    
    // Debug logging
    console.log('Current sessionNotes:', sessionNotes);
    console.log('Sessions:', sessions.map(s => ({ id: s.id, name: s.client.name })));
    
    return times.map(time => (
      <HStack key={time} align="flex-start" spacing={4}>
        <Box w="80px" pt={2}>
          <Text fontWeight="bold" color="gray.600">
            {formatTime(time)}
          </Text>
        </Box>
        <VStack flex="1" align="stretch" spacing={2}>
          {sessions
            .filter(session => session.time === time)
            .map(session => {
              const hasNote = !!sessionNotes[session.id];
              console.log(`Session ${session.id} (${session.client.name}): hasNote = ${hasNote}, sessionNotes[${session.id}] = ${sessionNotes[session.id]}`);
              
              return (
                <SessionCard
                  key={session.id}
                  session={session}
                  temporaryChanges={temporaryChanges}
                  selectedDate={selectedDate}
                  onEdit={handleEditSession}
                  onAddNote={handleAddSessionNote}
                  isDeckConfirmed={isDeckConfirmed}
                  hasNote={hasNote}
                  makeupSessions={makeupSessions}
                />
              );
            })}
        </VStack>
      </HStack>
    ));
  };

  // Helper function to group pending makeup sessions by date
  const getPendingMakeupSessionsByDate = () => {
    const groupedSessions = {};
    pendingMakeupSessions.forEach(session => {
      const date = session.attendance_date;
      if (!groupedSessions[date]) {
        groupedSessions[date] = [];
      }
      groupedSessions[date].push(session);
    });
    
    // Sort sessions within each date by time
    Object.keys(groupedSessions).forEach(date => {
      groupedSessions[date].sort((a, b) => parseInt(a.time) - parseInt(b.time));
    });
    
    return groupedSessions;
  };
  
  return (
    <Box>
      {/* Header with date navigation and controls */}
      <HStack justify="space-between" mb={6} align="center">
        <HStack>
          <IconButton
            icon={<ChevronLeftIcon />}
            onClick={() => {
              const newDate = new Date(selectedDate);
              newDate.setDate(selectedDate.getDate() - 1);
              setSelectedDate(newDate);
            }}
            aria-label="Previous day"
            //isDisabled={isDeckConfirmed}
          />
          <Popover>
            <PopoverTrigger>
            <Heading 
                size={{ base: "sm", md: "md" }} 
                cursor="pointer" 
                display="flex" 
                alignItems="center"
            >
                <Box display={{ base: "none", md: "block" }}>
                    {selectedDate.toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                    })}
                </Box>
                <Box display={{ base: "block", md: "none" }} noOfLines={2} minW="50px">
                    {selectedDate.toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric'
                    })}
                </Box>
                <CalendarIcon ml={2} />
            </Heading>
            </PopoverTrigger>
            <PopoverContent>
              <PopoverArrow />
              <PopoverCloseButton />
              <PopoverBody>
                <FormControl>
                  <FormLabel>Select a date</FormLabel>
                  <Input
                    type="date"
                    value={getLocalDateString(selectedDate)}
                    onChange={(e) => {
                      const [year, month, day] = e.target.value.split('-').map(Number);
                      setSelectedDate(new Date(year, month - 1, day));
                    }}
                    //isDisabled={isDeckConfirmed}
                  />
                </FormControl>
              </PopoverBody>
            </PopoverContent>
          </Popover>
          <IconButton
            icon={<ChevronRightIcon />}
            onClick={() => {
              const newDate = new Date(selectedDate);
              newDate.setDate(selectedDate.getDate() + 1);
              setSelectedDate(newDate);
            }}
            aria-label="Next day"
            //isDisabled={isDeckConfirmed}
          />
        </HStack>
  
        {!isAttendanceConfirmed && (
        <VStack 
            spacing={4} 
            align="stretch"
            display={{ base: "flex", md: "none" }} // Mobile layout
        >
            {!isDeckConfirmed ? (
            <Button
                colorScheme="blue"
                onClick={handleConfirmDeck}
                isLoading={isLoading}
                isDisabled={!isWithinOneDayOfSession(selectedDate)}
                size="sm"
            >
                Confirm Deck
            </Button>
            ) : (
            <Button
                colorScheme="green"
                onClick={() => setIsConfirmModalOpen(true)}
                isLoading={isLoading}
                isDisabled={!canConfirmAttendance()}
                size="sm"
            >
                Confirm Attendance
            </Button>
            )}

            {/* Cancel All Button - HIDDEN: Therapists can no longer bulk cancel */}
            {false && canBulkAction && (
            <Button
                colorScheme="red"
                variant="outline"
                onClick={() => setIsBulkCancelModalOpen(true)}
                isDisabled={sessions.length === 0}
                size="sm"
            >
                Cancel All
            </Button>
            )}
        </VStack>
        )}

        {/* Desktop layout */}
        {!isAttendanceConfirmed && (
        <HStack 
            spacing={4}
            display={{ base: "none", md: "flex" }}
        >
            {/* Original button layout */}
            {!isDeckConfirmed ? (
            <Button
                colorScheme="blue"
                onClick={handleConfirmDeck}
                isLoading={isLoading}
                isDisabled={!isWithinOneDayOfSession(selectedDate)}
            >
                Confirm Deck
            </Button>
            ) : (
            <Button
                colorScheme="green"
                onClick={() => setIsConfirmModalOpen(true)}
                isLoading={isLoading}
                isDisabled={!canConfirmAttendance()}
            >
                Confirm Attendance
            </Button>
            )}

            {/* Cancel All Sessions Button - HIDDEN: Therapists can no longer bulk cancel */}
            {false && canBulkAction && (
            <Button
                colorScheme="red"
                variant="outline"
                onClick={() => setIsBulkCancelModalOpen(true)}
                isDisabled={sessions.length === 0}
            >
                Cancel All Sessions
            </Button>
            )}
        </HStack>
        )}
      </HStack>
  
      {/* Tabs for Current Sessions and Pending Makeup Sessions */}
      <Tabs onChange={(index) => setView(index === 0 ? 'current' : 'pending-makeup')} mb={4}>
        <TabList>
          <Tab>Current Day</Tab>
          <Tab>Pending Makeup Sessions</Tab>
        </TabList>
        
        <TabPanels>
          <TabPanel px={0}>
            {/* Current Sessions */}
            <VStack align="stretch" spacing={4}>
              {isLoading ? (
                <Center py={8}>
                  <Spinner />
                </Center>
              ) : sessions.length === 0 ? (
                <Text textAlign="center" color="gray.500" py={8}>
                  No sessions scheduled for this day
                </Text>
              ) : (
                renderTimeSlots()
              )}
            </VStack>
          </TabPanel>
          
          <TabPanel px={0}>
            {/* Pending Makeup Sessions */}
            <VStack align="stretch" spacing={4}>
              <HStack justify="space-between" align="center">
                <Heading size="md">Pending Makeup Sessions</Heading>
                <HStack>
                  <Text fontSize="sm">Show last:</Text>
                  <Select
                    value={makeupDateRange}
                    onChange={(e) => setMakeupDateRange(parseInt(e.target.value))}
                    size="sm"
                    maxW="120px"
                  >
                    <option value={7}>7 days</option>
                    <option value={14}>14 days</option>
                    <option value={30}>30 days</option>
                    <option value={60}>60 days</option>
                    <option value={90}>90 days</option>
                  </Select>
                </HStack>
              </HStack>
              
              {pendingMakeupLoading ? (
                <Center py={8}>
                  <Spinner size="lg" />
                </Center>
              ) : (
                <VStack align="stretch" spacing={4}>
                  {Object.entries(getPendingMakeupSessionsByDate())
                    .sort(([dateA], [dateB]) => new Date(dateB) - new Date(dateA)) // Sort dates descending
                    .map(([date, dateSessions]) => (
                    <Box key={date} p={4} borderWidth={1} borderRadius="md">
                      <Heading size="sm" mb={4}>
                        {parseLocalDateString(date).toLocaleDateString('en-US', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                        <Badge ml={2} colorScheme="orange">{dateSessions.length} pending</Badge>
                      </Heading>
                                             <VStack align="stretch" spacing={2}>
                         {dateSessions.map(session => (
                           <SessionCard
                             key={`${session.id}-${session.attendance_date}`}
                             session={session}
                             temporaryChanges={temporaryChanges}
                             selectedDate={parseLocalDateString(session.attendance_date)}
                             onEdit={handleEditSession}
                             onAddNote={handleAddSessionNote}
                             isDeckConfirmed={false} // Allow editing makeup sessions
                             hasNote={!!sessionNotes[session.id]}
                             makeupSessions={makeupSessions}
                             isPendingMakeup={true}
                             onMarkOptional={(session) => {
                               setSelectedOptionalSession(session);
                               setIsOptionalMakeupModalOpen(true);
                             }}
                           />
                         ))}
                       </VStack>
                    </Box>
                  ))}
                  
                  {pendingMakeupSessions.length === 0 && (
                    <Box textAlign="center" py={8}>
                      <Text color="gray.500">No pending makeup sessions found in the last {makeupDateRange} days</Text>
                    </Box>
                  )}
                </VStack>
              )}
            </VStack>
          </TabPanel>
        </TabPanels>
      </Tabs>
  
      {/* Edit Session Modal */}
      <Modal 
        isOpen={isEditModalOpen} 
        onClose={() => {
          setIsEditModalOpen(false);
          setMakeupDate(null);
          setEditSession(null);
          setIsCancelledByClient(true);
          setNoMakeupRequired(false);
        }}
        size="lg"
        >
        <ModalOverlay />
        <ModalContent>
            <ModalHeader>
            Modify Session
            {editSession && ` - ${editSession.client.name}`}
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
            <VStack spacing={4}>
                <FormControl>
                    <FormLabel>Status</FormLabel>
                    <Select
                        value={editSession?.temporaryStatus || ''}
                        onChange={(e) => setEditSession({
                        ...editSession,
                        temporaryStatus: e.target.value
                        })}
                    >
                        {editSession?.attendance ? (
                        // Show these options if session already has attendance
                        <>
                            <option value="">Re-Open Session</option>
                            <option value="cancelled">Change to Cancelled</option>
                            <option value="absent">Change to Late Cancellation</option>
                            {!isSupposedMakeupSession(editSession) && (
                              <option value="makeup">Schedule Make-up</option>
                            )}
                        </>
                        ) : (
                        // Show these options if session has no attendance
                        <>
                            <option value="">Select status</option>
                            <option value="cancelled">Mark as Cancelled</option>
                            <option value="absent">Mark as Late Cancellation</option>
                            {!isSupposedMakeupSession(editSession) && (
                              <option value="makeup">Schedule Make-up</option>
                            )}
                        </>
                        )}
                    </Select>
                </FormControl>

                {isSupposedMakeupSession(editSession) &&
                  (editSession?.temporaryStatus === 'cancelled' ||
                    editSession?.temporaryStatus === 'absent') && (
                  <VStack spacing={3} align="stretch">
                    <Box bg="red.50" p={3} borderRadius="md" borderWidth={1} borderColor="red.200">
                      <Text fontSize="sm" color="red.700" fontWeight="medium">
                        This is a supposed makeup session being cancelled or late-cancelled.
                      </Text>
                      <Text fontSize="sm" color="red.600" mt={1}>
                        Charge the ₱950 no-show fee, or mark it as cancelled by the therapist (fee
                        waived; original session stops requiring makeup).
                      </Text>
                      <Text fontSize="xs" color="gray.600" mt={2}>
                        Original cancelled session:{' '}
                        {editSession.makeup_for
                          ? new Date(editSession.makeup_for).toLocaleDateString('en-US', {
                              weekday: 'long',
                              month: 'long',
                              day: 'numeric',
                              year: 'numeric',
                            })
                          : 'Unknown Date'}
                      </Text>
                    </Box>

                    <FormControl>
                      <Checkbox
                        isChecked={cancelledMakeupByTherapist}
                        onChange={(e) => {
                          const on = e.target.checked;
                          setCancelledMakeupByTherapist(on);
                          if (on) {
                            setPaidNoShowFee(false);
                          }
                        }}
                        colorScheme="blue"
                      >
                        Cancelled by therapist (waive no-show fee; original session stops requiring makeup)
                      </Checkbox>
                      <Text fontSize="xs" color="gray.500" mt={1} pl={6}>
                        Only check if you—the therapist/office—are cancelling this supposed makeup slot,
                        not because the client no-showed.
                      </Text>
                    </FormControl>

                    {!cancelledMakeupByTherapist && (
                      <FormControl>
                        <Checkbox
                          isChecked={paidNoShowFee}
                          onChange={(e) => {
                            setPaidNoShowFee(e.target.checked);
                            if (e.target.checked) {
                              setCancelledMakeupByTherapist(false);
                            }
                          }}
                          colorScheme="orange"
                        >
                          Paid No-Show Fee (₱950)
                        </Checkbox>
                        <Text fontSize="xs" color="gray.500" mt={1} pl={6}>
                          Check this once the client has paid the no-show fee
                        </Text>
                      </FormControl>
                    )}
                  </VStack>
                )}

                {(editSession?.temporaryStatus === 'cancelled' || editSession?.temporaryStatus === 'absent') &&
                  !isSupposedMakeupSession(editSession) && (
                  <VStack spacing={3}>
                    <FormControl>
                      <HStack justify="space-between" align="center">
                        <VStack align="start" spacing={1}>
                          <Text fontSize="sm" fontWeight="medium">
                            {editSession?.temporaryStatus === 'absent' 
                              ? "Late cancellation by client. Make-up session is required"
                              : isCancelledByClient 
                                ? "Cancelled by client. Make-up session is required" 
                                : "Cancelled by therapist. Make-up session is optional"}
                          </Text>
                        </VStack>
                        <Switch
                          isChecked={editSession?.temporaryStatus === 'absent' ? true : isCancelledByClient}
                          onChange={(e) => setIsCancelledByClient(e.target.checked)}
                          colorScheme="blue"
                          isDisabled={editSession?.temporaryStatus === 'absent'}
                        />
                      </HStack>
                    </FormControl>

                    {isCancelledByClient && editSession?.temporaryStatus !== 'absent' && (
                      <FormControl>
                        <HStack spacing={2} align="center">
                          <Checkbox
                            isChecked={noMakeupRequired}
                            onChange={(e) => setNoMakeupRequired(e.target.checked)}
                            colorScheme="blue"
                          >
                            <Text fontSize="sm">No makeup session required</Text>
                          </Checkbox>
                          <Tooltip 
                            label="Check this if you won't require a makeup session for this cancellation"
                            placement="top"
                            hasArrow
                          >
                            <InfoIcon color="gray.400" boxSize={3} />
                          </Tooltip>
                        </HStack>
                      </FormControl>
                    )}
                  </VStack>
                )}

                {editSession?.temporaryStatus === 'makeup' && (
                <>
                    <VStack spacing={3}>
                      <FormControl>
                        <HStack justify="space-between" align="center">
                          <VStack align="start" spacing={1}>
                            <Text fontSize="sm" fontWeight="medium">
                              {isCancelledByClient 
                                ? "This is a makeup session for a client cancellation" 
                                : "This is a makeup session for a therapist cancellation"}
                            </Text>
                          </VStack>
                          <Switch
                            isChecked={isCancelledByClient}
                            onChange={(e) => setIsCancelledByClient(e.target.checked)}
                            colorScheme="blue"
                          />
                        </HStack>
                      </FormControl>

                      {/* Discretionary no-makeup checkbox - only show when client cancelled */}
                      {isCancelledByClient && (
                        <FormControl>
                          <HStack spacing={2} align="center">
                            <Checkbox
                              isChecked={noMakeupRequired}
                              onChange={(e) => setNoMakeupRequired(e.target.checked)}
                              colorScheme="blue"
                            >
                              <Text fontSize="sm">No makeup session required</Text>
                            </Checkbox>
                            <Tooltip 
                              label="Check this if you won't require a makeup session for this cancellation"
                              placement="top"
                              hasArrow
                            >
                              <InfoIcon color="gray.400" boxSize={3} />
                            </Tooltip>
                          </HStack>
                        </FormControl>
                      )}
                    </VStack>

                    <FormControl>
                    <FormLabel>Make-up Date</FormLabel>
                    <Input
                        type="date"
                        value={makeupDate || ''}
                        onChange={(e) => setMakeupDate(e.target.value)}
                        min={getLocalDateString(new Date())}
                    />
                    </FormControl>

                    <FormControl>
                    <FormLabel>New Time</FormLabel>
                    <Select
                        value={editSession?.temporaryTime || ''}
                        onChange={(e) => setEditSession({
                        ...editSession,
                        temporaryTime: e.target.value
                        })}
                    >
                        <option value="">Select new time</option>
                        {Array.from({ length: 11 }, (_, i) => `${i + 8}:00`).map(time => (
                        <option key={time} value={time}>{formatTime(time)}</option>
                        ))}
                    </Select>
                    </FormControl>
                </>
                )}

                {editSession?.temporaryStatus === 'absent' && (
                  <>
                  <FormControl>
                    <Checkbox
                      isChecked={graceCancellation}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        if (checked) {
                          setWaivedLateCancellation(false);
                        }
                        setGraceCancellation(checked);
                      }}
                      isDisabled={
                        (!graceCancellation &&
                          graceCancellationUsedCount >= GRACE_CANCELLATIONS_PER_YEAR) ||
                        waivedLateCancellation
                      }
                      colorScheme="teal"
                    >
                      Grace Cancellation (waive ₱950 fee)
                    </Checkbox>
                    <Text fontSize="xs" color="gray.500" mt={1} pl={6}>
                      {getGraceCancellationHelperText(graceCancellationUsedCount, graceCancellation)}
                      . Makeup requirement still applies.
                    </Text>
                  </FormControl>
                  <FormControl>
                    <Checkbox
                      isChecked={waivedLateCancellation}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        if (checked) {
                          setGraceCancellation(false);
                        }
                        setWaivedLateCancellation(checked);
                      }}
                      isDisabled={graceCancellation}
                      colorScheme="cyan"
                    >
                      Waive Late Cancellation (special cases, e.g. floods)
                    </Checkbox>
                    <Text fontSize="xs" color="gray.500" mt={1} pl={6}>
                      Waives the ₱950 fee without using a grace cancellation. Makeup requirement still applies.
                    </Text>
                  </FormControl>
                  </>
                )}

                <FormControl isRequired={editSession?.temporaryStatus === 'cancelled' || editSession?.temporaryStatus === 'absent' || editSession?.temporaryStatus === 'makeup'}>
                <FormLabel>
                  {editSession?.temporaryStatus === 'cancelled' ? 'Reason for Cancellation & Time Messaged' :
                   editSession?.temporaryStatus === 'absent' ? 'Reason for Late Cancellation & Time Messaged' :
                   editSession?.temporaryStatus === 'makeup' ? 'Reason for Cancellation & Time Messaged' :
                   'Notes'}
                </FormLabel>
                <Textarea
                    value={editSession?.temporaryNotes || ''}
                    onChange={(e) => setEditSession({
                    ...editSession,
                    temporaryNotes: e.target.value
                    })}
                    placeholder={
                      editSession?.temporaryStatus === 'cancelled' ? 'Enter reason for cancellation and time messaged' :
                      editSession?.temporaryStatus === 'absent' ? 'Enter reason for late cancellation and time messaged' :
                      editSession?.temporaryStatus === 'makeup' ? 'Enter reason for cancellation and time messaged' :
                      'Add any notes about the change'
                    }
                />
                </FormControl>
            </VStack>
            </ModalBody>
            <ModalFooter>
            <Button
                colorScheme="blue"
                mr={3}
                onClick={handleSaveEdit}
                isDisabled={
                  (editSession?.temporaryStatus === 'cancelled' || editSession?.temporaryStatus === 'absent' || editSession?.temporaryStatus === 'makeup') && 
                  !editSession?.temporaryNotes?.trim()
                }
            >
                Save Changes
            </Button>
            <Button variant="ghost" onClick={() => {
              setIsEditModalOpen(false);
              setMakeupDate(null);
              setEditSession(null);
              setIsCancelledByClient(true);
              setNoMakeupRequired(false);
              setGraceCancellation(false);
              setWaivedLateCancellation(false);
              setGraceCancellationUsedCount(0);
              setCancelledMakeupByTherapist(false);
              setPaidNoShowFee(false);
            }}>
                Cancel
            </Button>
            </ModalFooter>
        </ModalContent>
      </Modal>

      <SessionSummaryModal 
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={handleConfirmAttendance}
      />

      <SuccessModal 
        isOpen={isSuccessModalOpen}
        onClose={() => setIsSuccessModalOpen(false)}
        currentDate={selectedDate}
        nextDate={nextSessionDate}
      />
      <BulkCancelModal 
        isOpen={isBulkCancelModalOpen} 
        onClose={() => setIsBulkCancelModalOpen(false)}
        onConfirm={handleBulkCancel}
        selectedDate={selectedDate}
      />
      <SessionNotesModal
        isOpen={isSessionNotesModalOpen}
        onClose={() => setIsSessionNotesModalOpen(false)}
        session={selectedSessionForNotes}
        therapistId={therapistId}
        onSave={handleSessionNoteSaved}
      />
      
      {/* Mark Makeup as Optional Confirmation Modal */}
      <Modal 
        isOpen={isOptionalMakeupModalOpen} 
        onClose={() => {
          setIsOptionalMakeupModalOpen(false);
          setSelectedOptionalSession(null);
        }}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Mark Makeup as Optional</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="start">
              {selectedOptionalSession && (
                <Text fontWeight="medium">
                  Client: {selectedOptionalSession.client.name}
                </Text>
              )}
              <Text>
                By continuing, this session will be removed from the pending required make-up list. The session will no longer appear in the "Pending Makeup Sessions" tab.
              </Text>
              <Text fontWeight="bold" color="orange.600">
                Continue?
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button 
              colorScheme="orange" 
              mr={3} 
              onClick={handleMarkMakeupOptional}
            >
              Yes, Mark as Optional
            </Button>
            <Button 
              variant="ghost" 
              onClick={() => {
                setIsOptionalMakeupModalOpen(false);
                setSelectedOptionalSession(null);
              }}
            >
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default CurrentDayDeck;